// The row's panes: 1–4, each split right or down from another (the layout tree in tabs.ts), each with its tab bar,
// 1px apart, sized by their dividers (drag or arrow keys to resize, double-click to make a split even again).
// Panes are laid out flat and placed by the tree. No row shown: the empty pane. Phones show the focused pane only.
// What the machine changes (an agent's split, from another device too) moves: the panes make room, a new one comes in
// from its far edge as the divider travels to its place, and one taken away goes out the same way.

import { useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent as RPointerEvent } from 'react';
import { PaneView } from './Pane';
import { GLIDE_MS, LAYOUT_SETTLED, reducedMotion } from './motion';
import { current, isBlank, type Group, type Layout, type TabsState } from './tabs';
import { useActions } from './Workspace';

const MIN = 0.12; // no pane smaller than 12% of its split

type Box = { x: number; y: number; w: number; h: number };
/** A pane's place; `hidden` while another fills the workspace (zoomed), kept mounted behind it. */
type Cell = Box & { pane: number; hidden?: boolean };
type Divider = { path: number[]; k: number; dir: 'row' | 'col'; box: Box; sizes: number[]; at: number };

/** Where every pane and divider goes, as fractions of the whole area. */
function place(l: Layout, box: Box, path: number[], cells: Cell[], dividers: Divider[]) {
  if ('pane' in l) {
    cells.push({ ...box, pane: l.pane });
    return;
  }
  let off = 0;
  l.kids.forEach((kid, k) => {
    const f = l.sizes[k];
    const sub = l.dir === 'row' ? { x: box.x + off * box.w, y: box.y, w: f * box.w, h: box.h } : { x: box.x, y: box.y + off * box.h, w: box.w, h: f * box.h };
    place(kid, sub, [...path, k], cells, dividers);
    off += f;
    if (k < l.kids.length - 1) dividers.push({ path, k, dir: l.dir, box, sizes: l.sizes, at: off });
  });
}

type Edge = 'left' | 'right' | 'top' | 'bottom';
const EPS = 1e-6;
const within = (o: Box, i: Box) => i.x >= o.x - EPS && i.y >= o.y - EPS && i.x + i.w <= o.x + o.w + EPS && i.y + i.h <= o.y + o.h + EPS;
const sameBox = (a: Box, b: Box) => Math.abs(a.x - b.x) < EPS && Math.abs(a.y - b.y) < EPS && Math.abs(a.w - b.w) < EPS && Math.abs(a.h - b.h) < EPS;

/** The edge of `outer` that `inner`, a part of it, lies against away from the rest: a pane split off comes in from
 *  there, and one taken away goes out there. */
function farEdge(inner: Box, outer: Box): Edge | null {
  if (inner.x > outer.x + EPS) return 'right';
  if (inner.x + inner.w < outer.x + outer.w - EPS) return 'left';
  if (inner.y > outer.y + EPS) return 'bottom';
  if (inner.y + inner.h < outer.y + outer.h - EPS) return 'top';
  return null;
}

type Drawn = { key: string; tabs: number[]; index: number; box?: Box };
type Ghost = { key: string; box: Box; edge: Edge | null; until: number };

/** Each pane's key: kept while it holds the same sessions (panes before it went, or it moved in the tree), so its
 *  terminal stays put; else its place's, while a pane was there; else a new one. */
function paneKeys(g: Group, before: Drawn[]): string[] {
  const free = new Set(before);
  const keys = g.panes.map((p, i) => {
    const ids = p.tabs.filter((id) => !isBlank(id));
    let best: Drawn | null = null;
    let most = 0;
    for (const d of free) {
      const n = ids.filter((id) => d.tabs.includes(id)).length;
      if (n > most || (n === most && n > 0 && d.index === i)) [best, most] = [d, n];
    }
    if (best) free.delete(best);
    return best?.key;
  });
  let n = Math.max(-1, ...before.map((d) => Number(d.key.split(':')[1]))) + 1;
  return keys.map((k, i) => {
    if (k) return k;
    const there = [...free].find((d) => d.index === i);
    if (there) return free.delete(there), there.key;
    return `${g.id}:${n++}`;
  });
}

/**
 * Each pane's key, and while the machine's news moves them (`glide`, not on a phone) or a pane zooms in or out: the
 * panes coming in (from which edge), the ones going (drawn where they were, empty, for GLIDE_MS) and whether the panes
 * are gliding (terminals hold their size meanwhile, and fit once it settles: LAYOUT_SETTLED); `zooming`: a zoom just
 * changed (with Reduce Motion the panes cross-fade instead).
 */
function usePaneMotion(g: Group | null, cells: Cell[], glide: boolean, zoomed: boolean) {
  const drawn = useRef<{ id: number; panes: Drawn[]; zoomed: boolean } | null>(null);
  const zoomUntil = useRef(0);
  const ghosts = useRef(new Map<string, Ghost>());
  const until = useRef(0);
  const [tick, setTick] = useState(0);
  const m = useMemo(() => {
    const now = performance.now();
    const before = g && drawn.current?.id === g.id ? drawn.current.panes : null;
    const keys = g ? paneKeys(g, before ?? []) : ['0:0'];
    const enter = new Map<string, Edge | null>();
    if (!before) ghosts.current.clear();
    const zoomChanged = !!before && drawn.current!.zoomed !== zoomed;
    if (zoomChanged) zoomUntil.current = now + GLIDE_MS;
    if (g && before && (glide || zoomChanged)) {
      const boxOf = new Map(cells.map((c) => [keys[c.pane], c as Box]));
      let moved = false;
      for (const k of keys) {
        const box = boxOf.get(k);
        const was = before.find((d) => d.key === k);
        if (was) moved ||= !!box && !!was.box && !sameBox(box, was.box);
        else if (box) enter.set(k, farEdge(box, before.find((d) => d.box && within(d.box, box))?.box ?? box));
      }
      for (const d of before) {
        if (keys.includes(d.key) || !d.box) continue;
        const into = cells.find((c) => within(c, d.box!));
        ghosts.current.set(d.key, { key: d.key, box: d.box, edge: into ? farEdge(d.box, into) : null, until: now + GLIDE_MS });
        moved = true;
      }
      if (moved || enter.size) until.current = now + GLIDE_MS;
    }
    for (const [k, x] of ghosts.current) if (x.until <= now || keys.includes(k)) ghosts.current.delete(k);
    return { keys, enter, ghosts: [...ghosts.current.values()], gliding: until.current > now && !reducedMotion(), zooming: zoomUntil.current > now };
    // cells follow g and zoomed; tick redraws once the glide is over
  }, [g, glide, zoomed, tick]);

  useLayoutEffect(() => {
    if (g) drawn.current = { id: g.id, zoomed, panes: m.keys.map((key, index) => ({ key, index, tabs: g.panes[index].tabs, box: cells.find((c) => c.pane === index) })) };
    const left = Math.max(until.current, zoomUntil.current, ...m.ghosts.map((x) => x.until)) - performance.now();
    if (left <= 0) return;
    const t = setTimeout(() => {
      setTick((n) => n + 1);
      window.dispatchEvent(new Event(LAYOUT_SETTLED));
    }, left);
    return () => clearTimeout(t);
  }, [m]);
  return m;
}

const pct = (v: number) => `${v * 100}%`;
/** A pane's place, 1px in from any edge it shares with another (the gap shows the line under it). */
const cellStyle = (c: Box) => {
  const l = c.x > 1e-6 ? 1 : 0;
  const t = c.y > 1e-6 ? 1 : 0;
  return { left: `calc(${pct(c.x)} + ${l}px)`, top: `calc(${pct(c.y)} + ${t}px)`, width: `calc(${pct(c.w)} - ${l}px)`, height: `calc(${pct(c.h)} - ${t}px)` };
};

export function Panes({ state, compact, glide = false }: { state: TabsState; compact: boolean; glide?: boolean }) {
  const a = useActions();
  const ref = useRef<HTMLDivElement>(null);
  const g = current(state);
  const cells: Cell[] = [];
  const dividers: Divider[] = [];
  if (compact || !g) cells.push({ x: 0, y: 0, w: 1, h: 1, pane: g?.focused ?? 0 });
  else place(g.layout, { x: 0, y: 0, w: 1, h: 1 }, [], cells, dividers);
  const multi = !compact && !!g && g.panes.length > 1;
  // zoomed (tmux's resize-pane -Z): the focused pane fills the workspace over the others, which stay mounted
  const zoomed = multi && !!g?.zoomed;
  if (zoomed) {
    cells.forEach((c, k) => (cells[k] = c.pane === g!.focused ? { x: 0, y: 0, w: 1, h: 1, pane: c.pane } : { ...c, hidden: true }));
    dividers.length = 0;
  }
  const motion = usePaneMotion(g, cells, glide && !compact, zoomed);

  /** The two panes beside a divider trade their share. */
  const moveTo = (d: Divider, at: number) => {
    const start = d.sizes.slice(0, d.k).reduce((x, y) => x + y, 0);
    const pair = d.sizes[d.k] + d.sizes[d.k + 1];
    const left = Math.min(Math.max(at - start, MIN), pair - MIN);
    const next = [...d.sizes];
    next[d.k] = left;
    next[d.k + 1] = pair - left;
    a.resize(d.path, next);
  };

  const drag = (d: Divider) => (e: RPointerEvent<HTMLDivElement>) => {
    const area = ref.current?.getBoundingClientRect();
    if (!area || e.button !== 0) return;
    e.preventDefault();
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);
    const move = (ev: PointerEvent) =>
      moveTo(d, d.dir === 'row' ? ((ev.clientX - area.left) / area.width - d.box.x) / d.box.w : ((ev.clientY - area.top) / area.height - d.box.y) / d.box.h);
    const up = () => {
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
    };
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
  };

  /** Arrow keys on a focused divider move it 2% at a time. */
  const key = (d: Divider) => (e: KeyboardEvent) => {
    const back = d.dir === 'row' ? 'ArrowLeft' : 'ArrowUp';
    const fwd = d.dir === 'row' ? 'ArrowRight' : 'ArrowDown';
    const step = e.key === back ? -0.02 : e.key === fwd ? 0.02 : 0;
    if (!step) return;
    e.preventDefault();
    moveTo(d, d.at + step);
  };

  return (
    <main ref={ref} className="ws-panes" data-testid="panes" data-layout={g?.panes.length ?? 0} data-multi={multi ? '' : undefined} data-gliding={motion.gliding ? '' : undefined} data-zoomed={zoomed ? '' : undefined} data-zooming={motion.zooming ? '' : undefined}>
      {/* panes on their way out: where they were, empty, under the ones making room */}
      {motion.ghosts.map((x) => (
        <div key={x.key} className="ws-cell ws-cell-gone" data-edge={x.edge ?? undefined} style={cellStyle(x.box)} aria-hidden="true">
          <div className="ws-pane">
            <div className="ws-strip ws-panebar" />
          </div>
        </div>
      ))}
      {cells.map((c) => (
        // keyed by what the pane holds: switching its tabs keeps its bar (a tab being dragged stays held), and a pane
        // that moves in the tree keeps its terminal
        <div
          key={motion.keys[c.pane] ?? c.pane}
          className="ws-cell"
          style={compact ? undefined : cellStyle(c)}
          data-enter={motion.enter.has(motion.keys[c.pane]) ? (motion.enter.get(motion.keys[c.pane]) ?? '') : undefined}
          // the zoomed pane stays over the others while it grows and shrinks
          data-front={c.pane === g?.focused && (zoomed || motion.zooming) ? '' : undefined}
          data-hidden={c.hidden ? '' : undefined}
          inert={c.hidden}
        >
          <PaneView
            index={c.pane}
            pane={g?.panes[c.pane] ?? null}
            focused={compact || c.pane === g?.focused}
            multi={multi}
          />
        </div>
      ))}
      {dividers.map((d) => {
        const row = d.dir === 'row';
        const pos = row ? d.box.x + d.at * d.box.w : d.box.y + d.at * d.box.h;
        const style = row ? { left: pct(pos), top: pct(d.box.y), height: pct(d.box.h) } : { top: pct(pos), left: pct(d.box.x), width: pct(d.box.w) };
        return (
          <div
            key={`${d.path.join('.')}:${d.k}`}
            className="ws-rz"
            data-axis={row ? undefined : 'row'}
            role="separator"
            aria-orientation={row ? 'vertical' : 'horizontal'}
            aria-label={row ? 'Resize panes' : 'Resize rows'}
            aria-valuenow={Math.round(d.at * 100)}
            tabIndex={0}
            data-testid="pane-resize"
            style={style}
            onPointerDown={drag(d)}
            onDoubleClick={() => a.resetSizes(d.path)}
            onKeyDown={key(d)}
          />
        );
      })}
    </main>
  );
}
